import { useEffect, useMemo, useRef } from 'react';
import { getThreadMessages } from '@/api/threads.api';
import {
  ANSWER_CARD_POLL_MAX_ATTEMPTS,
  POLL_INTERVAL_MS,
} from '@/constants/ui.constants';
import type { AnswerCardState } from '@/types/answerCard.types';
import type { ThreadMessage } from '@/types/messages';
import { logger } from '@/utils/logger';
import type { SetMessages } from './streamingRecovery';

interface UseAnswerCardPollerProps {
  projectId: string | null;
  threadId: string | null;
  messages: ThreadMessage[];
  setMessages: SetMessages;
}

// Local only, so the card stops waiting; a reload shows what was saved.
const TIMED_OUT: AnswerCardState = {
  status: 'failed',
  card: null,
  error: 'The answer card did not arrive in time.',
};

const hasPendingCard = (message: ThreadMessage) =>
  message.metadata?.answerCard?.status === 'pending';

/** `messages` with each still-pending card replaced from `cards`. */
function settleCards(
  messages: ThreadMessage[],
  cards: Map<string, AnswerCardState>,
): ThreadMessage[] {
  return messages.map((message) => {
    const answerCard = cards.get(message.id);
    return answerCard && hasPendingCard(message)
      ? { ...message, metadata: { ...message.metadata, answerCard } }
      : message;
  });
}

/**
 * Re-reads the thread while one of its assistant messages has a pending answer
 * card: review_reasoner writes the card into the saved message after the turn's
 * stream has closed. A card that is no longer pending is merged into its
 * message; a card still pending after ANSWER_CARD_POLL_MAX_ATTEMPTS reads is
 * given up on.
 */
export function useAnswerCardPoller({
  projectId,
  threadId,
  messages,
  setMessages,
}: UseAnswerCardPollerProps): void {
  // A string, so stream updates to the messages do not restart the timer.
  const pendingKey = useMemo(
    () =>
      messages
        .filter(
          (message) =>
            message.threadId === threadId &&
            message.role === 'assistant' &&
            hasPendingCard(message),
        )
        .map((message) => message.id)
        .join('\n'),
    [messages, threadId],
  );
  // Reads made for each card, kept while the timer restarts on a new key.
  const readsByCard = useRef(new Map<string, number>());

  useEffect(() => {
    if (!projectId || !threadId || !pendingKey) return;
    const pendingIds = pendingKey.split('\n');
    let cancelled = false;
    // A read slower than the interval is waited for, not stacked.
    let reading = false;

    const intervalId = setInterval(async () => {
      if (reading) return;
      const reads = readsByCard.current;
      const timedOut = pendingIds.filter(
        (id) => (reads.get(id) ?? 0) >= ANSWER_CARD_POLL_MAX_ATTEMPTS,
      );
      if (timedOut.length > 0) {
        clearInterval(intervalId);
        logger.warn(
          `[AnswerCardPoller] No answer card after ` +
            `${ANSWER_CARD_POLL_MAX_ATTEMPTS} reads of thread ${threadId}`,
        );
        setMessages((prev) =>
          settleCards(prev, new Map(timedOut.map((id) => [id, TIMED_OUT]))),
        );
        return;
      }
      pendingIds.forEach((id) => reads.set(id, (reads.get(id) ?? 0) + 1));
      reading = true;
      try {
        const fetched = await getThreadMessages(projectId, threadId);
        if (cancelled) return;
        const settled = new Map<string, AnswerCardState>();
        fetched.forEach((message) => {
          const answerCard = message.metadata?.answerCard as
            | AnswerCardState
            | undefined;
          if (
            pendingIds.includes(message.id) &&
            answerCard &&
            answerCard.status !== 'pending'
          ) {
            settled.set(message.id, answerCard);
          }
        });
        if (settled.size > 0) {
          setMessages((prev) => settleCards(prev, settled));
        }
      } catch (error) {
        logger.warn('[AnswerCardPoller] Failed to re-read the thread', error);
      } finally {
        reading = false;
      }
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [projectId, threadId, pendingKey, setMessages]);
}
